<template>
  <div class="card card-custom gutter-b" v-loading="loading">
    <ProductFormModal
      :visible="productModalVisible"
      :mode="productModalMode"
      :initial-data="editingProduct"
      :submit-handler="handleProductSubmit"
      @close="productModalVisible = false"
      @submit="onProductSubmitSuccess"
    />
    <ImportStockModal
      :visible="importStockModalVisible"
      :item="importStockItem"
      :submit-handler="handleImportStockSubmit"
      @close="importStockModalVisible = false"
      @submit="onImportStockSuccess"
    />
    <ExportStockModal
      :visible="exportStockModalVisible"
      :item="exportStockItem"
      :branch-options="branchOptions"
      :branch-options-loading="branchOptionsLoading"
      :submit-handler="handleExportStockSubmit"
      @close="exportStockModalVisible = false"
      @submit="onExportStockSuccess"
    />
    <ProductHistoryModal
      :visible="historyModalVisible"
      :type="historyModalType"
      :product="historyProduct"
      :data="historyData"
      :loading="historyLoading"
      @close="historyModalVisible = false"
    />
    <ProductBarcodeModal
      :visible="barcodeModalVisible"
      :barcode="selectedBarcode"
      :product-name="selectedProductName"
      @close="barcodeModalVisible = false"
    />

    <div class="card-header d-flex justify-content-between">
      <div class="card-title">
        <h3 class="card-label">Danh sách mặt hàng</h3>
      </div>
      <div class="card-title">
        <button
          type="button"
          class="btn btn-light-primary font-weight-bolder mr-2"
          :disabled="exporting"
          @click="handleExport"
        >
          <span class="svg-icon svg-icon-md"><i class="el-icon-download"></i></span>
          {{ exporting ? "Đang xuất..." : "Export" }}
        </button>
        <router-link
          :to="{ name: 'logisticsBarcodePrint' }"
          class="btn btn-light-primary font-weight-bolder mr-2"
        >
          <i class="fas fa-barcode"></i> In mã vạch
        </router-link>
        <button class="btn btn-primary font-weight-bold" @click="handleAddNew">
          <i class="el-icon-plus"></i> Thêm mới
        </button>
      </div>
    </div>

    <div class="card-body">
      <div class="row filter_form">
        <div class="col-md-3">
          <div class="form-group">
            <el-input
              v-model="searchQuery"
              placeholder="Tìm kiếm tên mặt hàng"
              clearable
              @input="handleSearchInput"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </div>
        </div>

        <div class="col-md-3">
          <div class="form-group">
            <el-select
              v-model="selectedType"
              class="w-100"
              clearable
              filterable
              placeholder="Chọn loại"
              @change="handleFilterType"
            >
              <el-option
                v-for="item in productTypeOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
        </div>

        <div class="col-md-3">
          <div class="form-group">
            <el-select
              v-model="selectedTypeDetail"
              class="w-100"
              clearable
              filterable
              placeholder="Chọn phân loại chi tiết"
              @change="handleFilterTypeDetail"
            >
              <el-option
                v-for="item in productTypeDetailOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
        </div>

        <div class="col-md-3">
          <div class="form-group">
            <el-select
              v-model="selectedReorderLevel"
              class="w-100"
              clearable
              placeholder="Trạng thái"
              @change="handleFilterReorderLevel"
            >
              <el-option
                v-for="item in reorderLevelOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
        </div>
      </div>

      <div class="table-responsive mt-4">
        <table class="table table-vertical-center table-bordered mb-0">
          <thead class="bg-light">
            <tr>
              <th class="text-left align-middle table-stt">STT</th>
              <th class="text-left align-middle">Tên mặt hàng</th>
              <th class="text-left align-middle">Loại</th>
              <th class="text-left align-middle">Phân loại chi tiết</th>
              <th class="text-left align-middle">Giá</th>
              <th class="text-left align-middle">Số lượng</th>
              <th class="text-left align-middle">Số lượng báo động</th>
              <th class="text-left align-middle">Trạng thái</th>
              <th class="text-left align-middle">Hành động</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="productList?.length > 0">
              <tr
                v-for="(item, index) of productList"
                :key="item?.id"
                :class="{ 'needs-reorder-row': isNeedReorder(item) }"
              >
                <td class="text-left align-middle">{{ (page - 1) * 20 + index + 1 }}</td>
                <td class="text-left align-middle">
                  <div class="d-flex align-items-center">
                    <p class="line-clamp-1 mb-0 text-break">{{ item.name }}</p>
                    <i
                      v-if="isNeedReorder(item)"
                      class="fas fa-exclamation-triangle reorder-warning-icon ml-2"
                      title="Sản phẩm cần mua"
                    ></i>
                  </div>
                </td>
                <td class="text-left align-middle">{{ item.type_label }}</td>
                <td class="text-left align-middle">{{ item.type_detail_label }}</td>
                <td class="text-left align-middle">{{ formatCurrency(item.price) }}</td>
                <td class="text-left align-middle">{{ item.quantity }}</td>
                <td class="text-left align-middle">{{ item.reorder_level }}</td>
                <td class="text-left align-middle">
                  <span :class="`${!item.is_hidden ? 'badge badge-success' : 'badge badge-secondary'}`">
                    {{ !item.is_hidden ? "Hiển thị" : "Ẩn" }}
                  </span>
                </td>
                <td class="text-left align-middle">
                  <button class="btn btn-icon btn-xs btn-outline-info" title="Sửa" @click="handleEdit(item)">
                    <i class="fas fa-pen-nib"></i>
                  </button>
                  <button class="btn btn-icon btn-xs btn-outline-danger ml-2" title="Xóa" @click="handleDelete(item)">
                    <i class="fas fa-trash"></i>
                  </button>
                  <button class="btn btn-icon btn-xs btn-outline-success ml-2" title="Nhập kho" @click="handleImportStock(item)">
                    <i class="fas fa-box"></i>
                  </button>
                  <button class="btn btn-icon btn-xs btn-outline-warning ml-2" title="Xuất kho" @click="handleExportStock(item)">
                    <i class="fas fa-dolly"></i>
                  </button>
                  <button class="btn btn-icon btn-xs btn-outline-info ml-2" title="Lịch sử giá" @click="handlePriceHistory(item)">
                    <i class="fas fa-chart-line"></i>
                  </button>
                  <button class="btn btn-icon btn-xs btn-outline-warning ml-2" title="Lịch sử kho" @click="handleStockHistory(item)">
                    <i class="fas fa-wave-square"></i>
                  </button>
                  <button
                    class="btn btn-icon btn-xs btn-outline-dark ml-2"
                    title="Mã vạch"
                    :disabled="!hasBarcode(item)"
                    @click="handleShowBarcode(item)"
                  >
                    <i class="fas fa-barcode"></i>
                  </button>
                </td>
              </tr>
            </template>

            <template v-else>
              <tr>
                <td colspan="9" class="text-center">Chưa có dữ liệu</td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="productList?.length > 0">
          <div class="d-flex justify-content-center align-items-center mt-4 mb-3">
            <paginate
              v-model="page"
              :page-count="lastPage"
              :page-range="3"
              :margin-pages="1"
              :click-handler="clickCallback"
              :prev-text="'Trước'"
              :next-text="'Sau'"
              :container-class="'pagination b-pagination'"
              :pageLinkClass="'page-link'"
              :next-link-class="'next-link-item'"
              :prev-link-class="'prev-link-item'"
              :prev-class="'page-link'"
              :next-class="'page-link'"
              :page-class="'page-item'"
            />
          </div>
          <div class="d-flex justify-content-end">
            <div>
              Tổng số bản ghi:
              <span class="ml-1 text-primary font-weight-bold">
                {{ this.total ?? productList.length }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  PRODUCT_TYPE_DETAIL_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
} from "@/core/option/logisticsOption";
import {
  GET_PRODUCTS,
  CREATE_PRODUCT,
  UPDATE_PRODUCT,
  DELETE_PRODUCT,
  IMPORT_STOCK,
  EXPORT_STOCK,
  GET_LOGISTIC_BRANCHES,
  GET_PRICE_HISTORY,
  GET_STOCK_HISTORY,
  EXPORT_STOCK_HISTORY,
} from "@/core/services/store/logistics/logistics.module";
import { exportExcel } from "@/core/filters";
import ProductFormModal from "./components/ProductFormModal.vue";
import ImportStockModal from "./components/ImportStockModal.vue";
import ExportStockModal from "./components/ExportStockModal.vue";
import ProductHistoryModal from "./components/ProductHistoryModal.vue";
import ProductBarcodeModal from "./components/ProductBarcodeModal.vue";

const HEADER_ROW_EXPORT_STOCK_HISTORY = [
  { key: "stt", value: "STT" },
  { key: "product_name", value: "Tên mặt hàng" },
  { key: "product_type_text", value: "Loại mặt hàng" },
  { key: "product_type_detail_text", value: "Phân loại chi tiết" },
  { key: "created_at", value: "Thời gian" },
  { key: "type_text", value: "Loại" },
  { key: "price", value: "Giá tại thời điểm" },
  { key: "quantity_before", value: "Số lượng trước" },
  { key: "quantity_after", value: "Tồn kho" },
  { key: "quantity_change", value: "Số lượng thay đổi" },
  { key: "branch_name", value: "Chi nhánh" },
  { key: "note", value: "Ghi chú" },
];

const EXPORT_STOCK_HISTORY_FILE_NAME = "Lịch sử xuất nhập kho.xlsx";

export default {
  name: "ProductManagement",
  components: {
    ProductFormModal,
    ImportStockModal,
    ExportStockModal,
    ProductHistoryModal,
    ProductBarcodeModal,
  },
  data() {
    return {
      loading: true,
      exporting: false,
      page: 1,
      lastPage: 1,
      total: 0,
      productList: [],
      searchQuery: "",
      selectedType: null,
      selectedTypeDetail: null,
      selectedReorderLevel: null,
      productTypeOptions: PRODUCT_TYPE_OPTIONS,
      productTypeDetailOptions: PRODUCT_TYPE_DETAIL_OPTIONS,
      reorderLevelOptions: [
        { value: 1, label: "Cần mua" },
        { value: 0, label: "Tất cả" },
      ],
      productModalVisible: false,
      productModalMode: "create",
      editingProduct: null,
      importStockModalVisible: false,
      importStockItem: null,
      exportStockModalVisible: false,
      exportStockItem: null,
      branchOptions: [],
      branchOptionsLoading: false,
      historyModalVisible: false,
      historyModalType: "price",
      historyProduct: null,
      historyData: [],
      historyLoading: false,
      barcodeModalVisible: false,
      selectedBarcode: "",
      selectedProductName: "",
      searchTimeout: null,
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Logistics", route: "" },
      { title: "Danh sách mặt hàng" },
    ]);
    this.getProductList();
    this.getBranchOptions();
  },
  methods: {
    formatCurrency(value) {
      if (!value) return "0 đ";
      const numericValue = Number(value);
      return `${new Intl.NumberFormat("vi-VN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number.isNaN(numericValue) ? 0 : numericValue)} đ`;
    },
    handleSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.handleSearch();
      }, 300);
    },
    handleSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.page = 1;
      this.getProductList();
    },
    handleFilterType() {
      this.page = 1;
      this.getProductList();
    },
    handleFilterTypeDetail() {
      this.page = 1;
      this.getProductList();
    },
    handleFilterReorderLevel() {
      this.page = 1;
      this.getProductList();
    },
    clickCallback(obj) {
      this.page = obj;
      this.getProductList();
    },
    getFilterParams() {
      return {
        name: (this.searchQuery || "").trim(),
        type: this.selectedType,
        type_detail: this.selectedTypeDetail,
        reorder_level: this.selectedReorderLevel,
      };
    },
    async getProductList() {
      this.loading = true;
      const payload = {
        page: this.page,
        limit: 20,
        ...this.getFilterParams(),
      };

      this.$store
        .dispatch(GET_PRODUCTS, payload)
        .then((res) => {
          if (!res.error) {
            this.page = res.pagination.current_page;
            this.lastPage = res.pagination.last_page;
            this.total = res.pagination.total;
            this.productList = res.data ?? [];
          }
        })
        .catch((e) => {
          console.log(e, "Error get product list");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async handleExport() {
      this.exporting = true;
      try {
        const res = await this.$store.dispatch(EXPORT_STOCK_HISTORY, this.getFilterParams());
        if (res?.error) throw new Error(res.message || "Không thể xuất dữ liệu");

        const dataExport = res.data ?? [];
        if (!dataExport.length) {
          this.$message.warning("Không có lịch sử xuất nhập kho để xuất");
          return;
        }
        exportExcel(HEADER_ROW_EXPORT_STOCK_HISTORY, dataExport, EXPORT_STOCK_HISTORY_FILE_NAME, true);
        this.$message.success("Xuất dữ liệu thành công");
      } catch (e) {
        this.$message.error(e?.message || "Không thể xuất dữ liệu");
      } finally {
        this.exporting = false;
      }
    },
    handleAddNew() {
      this.productModalMode = "create";
      this.editingProduct = null;
      this.productModalVisible = true;
    },
    handleEdit(item) {
      this.productModalMode = "edit";
      this.editingProduct = {
        id: item.id,
        name: item.name,
        price: item.price,
        type: item.type,
        type_detail: item.type_detail,
        description: item.description || "",
        logistic_product_status: item.logistic_product_status ?? 0,
        barcode: item.barcode ?? "",
        reorder_level: item.reorder_level ?? 0,
      };
      this.productModalVisible = true;
    },
    async handleProductSubmit(payload) {
      const res =
        this.productModalMode === "create"
          ? await this.$store.dispatch(CREATE_PRODUCT, payload)
          : await this.$store.dispatch(UPDATE_PRODUCT, payload);
      if (res?.error) {
        const error = new Error(res.message || "Có lỗi xảy ra");
        error.data = res.data;
        throw error;
      }
      return res;
    },
    onProductSubmitSuccess() {
      this.$message.success(
        this.productModalMode === "create"
          ? "Tạo mặt hàng thành công"
          : "Cập nhật mặt hàng thành công"
      );
      this.getProductList();
    },
    async handleDelete(item) {
      try {
        await this.$confirm(`Xóa mặt hàng ${item.name}?`, "Xác nhận", {
          confirmButtonText: "Đồng ý",
          cancelButtonText: "Bỏ qua",
          type: "warning",
        });
        const res = await this.$store.dispatch(DELETE_PRODUCT, item.id);
        if (!res.error) {
          this.$message.success("Xóa mặt hàng thành công");
          this.getProductList();
        }
      } catch (e) {
        if (e !== "cancel") {
          console.log("Delete product error", e);
          this.$message.error(e?.message || "Xóa mặt hàng thất bại");
        }
      }
    },
    handleImportStock(item) {
      this.importStockItem = { ...item };
      this.importStockModalVisible = true;
    },
    async handleImportStockSubmit(payload) {
      const res = await this.$store.dispatch(IMPORT_STOCK, payload);
      if (res?.error) {
        throw new Error(res.message || "Có lỗi xảy ra");
      }
      return res;
    },
    onImportStockSuccess() {
      this.$message.success("Cập nhật tồn kho thành công");
      this.getProductList();
    },
    async getBranchOptions() {
      this.branchOptionsLoading = true;
      try {
        const res = await this.$store.dispatch(GET_LOGISTIC_BRANCHES);
        if (!res?.error) {
          this.branchOptions = res?.data ?? [];
        }
      } catch (e) {
        console.log(e, "Error get branch options");
      } finally {
        this.branchOptionsLoading = false;
      }
    },
    handleExportStock(item) {
      this.exportStockItem = { ...item };
      this.exportStockModalVisible = true;
    },
    async handleExportStockSubmit(payload) {
      const res = await this.$store.dispatch(EXPORT_STOCK, payload);
      if (res?.error) {
        throw new Error(res.message || "Có lỗi xảy ra");
      }
      return res;
    },
    onExportStockSuccess() {
      this.$message.success("Xuất kho thành công");
      this.getProductList();
    },
    async handlePriceHistory(item) {
      this.historyProduct = { ...item };
      this.historyModalType = "price";
      this.historyModalVisible = true;
      this.historyData = [];
      this.historyLoading = true;
      try {
        const res = await this.$store.dispatch(GET_PRICE_HISTORY, item.id);
        if (!res?.error) {
          this.historyData = res.data ?? [];
        }
      } catch (e) {
        console.error("Error get price history", e);
      } finally {
        this.historyLoading = false;
      }
    },
    async handleStockHistory(item) {
      this.historyProduct = { ...item };
      this.historyModalType = "stock";
      this.historyModalVisible = true;
      this.historyData = [];
      this.historyLoading = true;
      try {
        const res = await this.$store.dispatch(GET_STOCK_HISTORY, item.id);
        if (!res?.error) {
          this.historyData = res.data ?? [];
        }
      } catch (e) {
        console.error("Error get stock history", e);
      } finally {
        this.historyLoading = false;
      }
    },
    handleShowBarcode(item) {
      if (!this.hasBarcode(item)) return;
      this.selectedBarcode = String(item.barcode);
      this.selectedProductName = item.name || "";
      this.barcodeModalVisible = true;
    },
    hasBarcode(item) {
      return (
        item?.barcode !== null &&
        item?.barcode !== undefined &&
        String(item.barcode).trim() !== ""
      );
    },
    isNeedReorder(item) {
      return Number(item?.quantity) < Number(item?.reorder_level);
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped>
.table-stt {
  width: 5%;
}

.filter_form {
  margin-top: 4px;
}

.filter_form .form-group {
  margin-bottom: 16px;
}

.needs-reorder-row td {
  background-color: #fff8f1;
}

.reorder-warning-icon {
  color: #ff8a1f;
  flex-shrink: 0;
  font-size: 1rem;
}

@media screen and (max-width: 767px) {
  .filter_form .form-group {
    margin-bottom: 12px;
  }
}
</style>
